"use client";

import { useEffect, useRef, useState } from "react";
import { DARK_BAND, GradText, SectionHead, SectionShell } from "./kit";
import { gsap, NOT_REDUCED, ScrollTrigger, useGSAP } from "./kit/motion";

const STEPS = [
  {
    id: "land",
    title: "A click lands.",
    desc: "Every tap carries context — device, country, referrer and any UTM tags on the short link. rii captures it server-side as the request arrives; no pixel, no script, nothing to block.",
  },
  {
    id: "resolve",
    title: "Resolved in milliseconds.",
    desc: "The routing engine picks the destination straight from memory, and the click keeps the UTM tags on the destination it served. Capturing analytics never slows the redirect — the visitor is already on their way.",
  },
  {
    id: "insight",
    title: "It becomes insight.",
    desc: "The event lands in your dashboard within seconds: clicks, devices, countries, referrers and campaign tags, with the short link’s UTMs and the destination’s side by side — per domain and per path.",
  },
  {
    id: "export",
    title: "Yours to query — and to take.",
    desc: "Read it in the dashboard, pull stats and a live click stream over the API, or download the raw click log as CSV or JSON. Your data stays yours — exportable and deletable anytime, never resold.",
  },
];

/** Mini-dashboard stat tiles; targets drive the scrubbed count-up. */
const TILES = [
  { label: "Human clicks", target: 12408, decimals: 0, suffix: "" },
  { label: "Mobile", target: 62, decimals: 0, suffix: "%" },
  { label: "Bots filtered", target: 1317, decimals: 0, suffix: "" },
];

/** Mini bar chart heights (percent of the chart area). */
const BAR_HEIGHTS = [40, 55, 35, 70, 52, 85, 64];

const EXPORT_CMD =
  '$ curl -OJ "https://api.rii.link/v1/export/request-logs?format=csv" -H "Authorization: Bearer $RII_KEY"';

const CARD_BG = "#1b1b26";
const CARD_BORDER = "rgba(255,255,255,0.12)";

function formatTileValue(value: number, decimals: number, suffix: string) {
  const fixed = decimals > 0 ? value.toFixed(decimals) : Math.round(value).toLocaleString("en-US");
  return `${fixed}${suffix}`;
}

/**
 * "Life of a click" — the /analytics signature scroll moment. Same proven
 * skeleton as PinnedRouteStorySection: sticky viewport + ScrollTrigger scrub
 * (NEVER pin: true — pin-spacers fight RowBand row-snapping and grid-relayout
 * measurement). Dark bands are opaque, so nothing inside the sticky track is a
 * [data-grid-block]; the dashboard cards carry their own opaque surfaces.
 * The right panel is a mini dashboard that builds itself step by step: the
 * click event appears, the route resolves, tiles count up + bars rise, and a
 * request-log export call types out.
 */
export default function ClickStorySection() {
  const trackRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);

  // Scrub layout only where it can run: md+ pointer scroll, motion allowed.
  // Default (SSR, mobile, reduced motion) is the static stacked story with
  // every dashboard element at its final state.
  const [enhanced, setEnhanced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(`${NOT_REDUCED} and (min-width: 768px)`);
    const update = () => setEnhanced(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // The track grows to 300vh when enhanced — tell RowBand/GridClearing and
  // ScrollTrigger the geometry changed. Skip the very first run: nothing has
  // changed yet on mount.
  const isFirstEnhancedRun = useRef(true);
  useEffect(() => {
    if (isFirstEnhancedRun.current) {
      isFirstEnhancedRun.current = false;
      return;
    }
    window.dispatchEvent(new Event("grid-relayout"));
    ScrollTrigger.refresh();
  }, [enhanced]);

  useGSAP(
    (_context, contextSafe) => {
      if (!enhanced) return;
      const track = trackRef.current;
      const panel = panelRef.current;
      const copyWrap = copyRef.current;
      if (!track || !panel || !copyWrap) return;

      // Build after webfonts settle (layout-final measurements) — and guard
      // against a stale async build after enhanced flips (revertOnUpdate).
      let disposed = false;
      let tl: gsap.core.Timeline | undefined;

      const build = contextSafe!(() => {
        if (disposed) return;

        const copies = gsap.utils.toArray<HTMLElement>("[data-cs-copy]", copyWrap);
        const event = panel.querySelector<HTMLElement>("[data-cs-event]");
        const route = panel.querySelector<HTMLElement>("[data-cs-route]");
        const routeLine = panel.querySelector<HTMLElement>("[data-cs-routeline]");
        const latency = panel.querySelector<HTMLElement>("[data-cs-latency]");
        const insight = panel.querySelector<HTMLElement>("[data-cs-insight]");
        const nums = gsap.utils.toArray<HTMLElement>("[data-cs-num]", panel);
        const bars = gsap.utils.toArray<HTMLElement>("[data-cs-bar]", panel);
        const exportRow = panel.querySelector<HTMLElement>("[data-cs-export]");
        const cmd = panel.querySelector<HTMLElement>("[data-cs-cmd]");
        const result = panel.querySelector<HTMLElement>("[data-cs-result]");
        if (
          copies.length !== STEPS.length ||
          !event ||
          !route ||
          !routeLine ||
          !latency ||
          !insight ||
          nums.length !== TILES.length ||
          bars.length !== BAR_HEIGHTS.length ||
          !exportRow ||
          !cmd ||
          !result
        )
          return;

        // Pre-states: future-step elements sit dimmed; numbers start at zero;
        // bars collapsed; export command empty. autoAlpha (not opacity class)
        // so a context revert restores the static final state exactly.
        gsap.set(copies.slice(1), { opacity: 0 });
        gsap.set([event, route, insight, exportRow], { autoAlpha: 0.12 });
        gsap.set(routeLine, { scaleX: 0, transformOrigin: "left center" });
        gsap.set(latency, { autoAlpha: 0 });
        gsap.set(bars, { scaleY: 0.06, transformOrigin: "bottom center" });
        gsap.set(result, { autoAlpha: 0 });
        nums.forEach((el) => {
          el.textContent = formatTileValue(
            0,
            Number(el.dataset.decimals),
            el.dataset.suffix ?? "",
          );
        });
        cmd.textContent = "$";

        tl = gsap.timeline({
          defaults: { ease: "none" },
          scrollTrigger: {
            trigger: track,
            start: "top top",
            end: "bottom bottom",
            scrub: 0.6,
          },
        });

        // Step 01 — the click event appears.
        tl.to(event, { autoAlpha: 1, duration: 0.35 }, 0.2);
        tl.fromTo(event, { y: -10 }, { y: 0, duration: 0.35 }, 0.2);

        // Step 02 — the route resolves: line fills, status badge pops.
        tl.to(route, { autoAlpha: 1, duration: 0.25 }, 1.1);
        tl.to(routeLine, { scaleX: 1, duration: 0.5 }, 1.2);
        tl.to(latency, { autoAlpha: 1, duration: 0.2 }, 1.65);

        // Step 03 — the dashboard fills: tiles count up, bars rise.
        tl.to(insight, { autoAlpha: 1, duration: 0.25 }, 2.1);
        nums.forEach((el, i) => {
          const proxy = { v: 0 };
          tl!.to(
            proxy,
            {
              v: TILES[i].target,
              duration: 0.6,
              onUpdate: () => {
                el.textContent = formatTileValue(proxy.v, TILES[i].decimals, TILES[i].suffix);
              },
            },
            2.2 + i * 0.08,
          );
        });
        tl.to(bars, { scaleY: 1, duration: 0.45, stagger: 0.04 }, 2.3);

        // Step 04 — the export API call types out, result lands.
        tl.to(exportRow, { autoAlpha: 1, duration: 0.25 }, 3.1);
        tl.to(
          cmd,
          {
            scrambleText: { text: EXPORT_CMD, chars: "lowerCase", speed: 0.4 },
            duration: 0.55,
          },
          3.2,
        );
        tl.to(result, { autoAlpha: 1, duration: 0.2 }, 3.8);

        // Copy crossfades between steps.
        STEPS.forEach((_step, i) => {
          if (i === 0) return;
          tl!.to(copies[i - 1], { opacity: 0, duration: 0.18 }, i - 0.05);
          tl!.to(copies[i], { opacity: 1, duration: 0.22 }, i + 0.12);
        });
      });

      document.fonts.ready.then(build).catch(build);

      return () => {
        disposed = true;
        tl?.scrollTrigger?.kill();
        tl?.kill();
      };
    },
    { dependencies: [enhanced], scope: trackRef, revertOnUpdate: true },
  );

  return (
    <SectionShell id="ref-click-story" band="dark" decoAvoid className="max-md:bg-[#14141c]">
      <SectionHead
        center
        tone="light"
        gridFill={DARK_BAND}
        eyebrow="Life of a click"
        title={
          <>
            One tap. <GradText>Full picture.</GradText>
          </>
        }
        lead="Follow a single click from an Instagram bio in Berlin all the way into your dashboard — captured, resolved and counted within seconds."
      />

      <div ref={trackRef} className={`relative ${enhanced ? "h-[300vh]" : ""}`}>
        <div className={enhanced ? "sticky top-0 flex h-screen items-center" : ""}>
          <div className="grid w-full items-center gap-12 md:grid-cols-2">
            {/* Copy panel — crossfades per step when enhanced, stacks otherwise.
                min-w-0 on both columns: the nowrap mono lines (event, curl)
                would otherwise force the grid column to their full width. */}
            <div ref={copyRef} className="relative min-w-0">
              {STEPS.map((s, i) => (
                <div
                  key={s.id}
                  data-cs-copy
                  className={enhanced && i > 0 ? "absolute inset-0" : enhanced ? "" : "mb-10"}
                >
                  <div className="font-jetbrains text-[13px] tracking-[0.08em] text-white/45">
                    {String(i + 1).padStart(2, "0")} / 04
                  </div>
                  <h3 className="mt-3 text-[clamp(22px,2.4vw,30px)] font-bold tracking-[-0.02em] text-white">
                    {s.title}
                  </h3>
                  <p className="mt-3 max-w-[440px] text-[15.5px] leading-[1.65] text-white/70">
                    {s.desc}
                  </p>
                </div>
              ))}
            </div>

            {/* The mini dashboard. Own opaque surfaces — NOT grid blocks (sticky). */}
            <div ref={panelRef} className="flex min-w-0 flex-col gap-3">
              {/* 01 · the click event */}
              <div
                data-cs-event
                className="flex items-center gap-3 rounded-xl border px-5 py-3.5"
                style={{ borderColor: CARD_BORDER, backgroundColor: CARD_BG }}
              >
                <span className="h-2 w-2 shrink-0 rounded-full bg-brand-500 shadow-[0_0_8px_var(--color-brand-500)]" />
                <div className="min-w-0 flex-1">
                  <div className="font-jetbrains text-[12px] uppercase tracking-[0.09em] text-white/50">
                    Click event
                  </div>
                  <div className="mt-0.5 truncate font-jetbrains text-[13.5px] text-white/85">
                    android · DE · instagram.com · utm_campaign=spring
                  </div>
                </div>
              </div>

              {/* 02 · the route */}
              <div
                data-cs-route
                className="rounded-xl border px-5 py-3.5"
                style={{ borderColor: CARD_BORDER, backgroundColor: CARD_BG }}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="font-jetbrains text-[12px] uppercase tracking-[0.09em] text-white/50">
                    Routed
                  </div>
                  <div
                    data-cs-latency
                    className="rounded-full border border-brand-500/40 bg-brand-500/10 px-2.5 py-0.5 font-jetbrains text-[11.5px] font-semibold text-brand-500"
                  >
                    302
                  </div>
                </div>
                <div className="mt-2.5 flex items-center gap-3">
                  <span className="font-jetbrains text-[13px] text-white/85">rii.link/spring</span>
                  <span className="relative h-px flex-1 bg-white/15">
                    <span
                      data-cs-routeline
                      className="absolute inset-0 bg-brand-500 shadow-[0_0_8px_var(--color-brand-500)]"
                    />
                  </span>
                  <span className="font-jetbrains text-[13px] text-white/85">myshop.com/de</span>
                </div>
              </div>

              {/* 03 · the dashboard */}
              <div
                data-cs-insight
                className="rounded-xl border px-5 py-4"
                style={{ borderColor: CARD_BORDER, backgroundColor: CARD_BG }}
              >
                <div className="grid grid-cols-3 gap-3">
                  {TILES.map((t) => (
                    <div key={t.label} className="rounded-[10px] bg-white/[0.05] px-3 py-2.5">
                      <div
                        data-cs-num
                        data-decimals={t.decimals}
                        data-suffix={t.suffix}
                        className="font-jetbrains text-[17px] font-semibold tabular-nums text-white"
                      >
                        {formatTileValue(t.target, t.decimals, t.suffix)}
                      </div>
                      <div className="mt-0.5 text-[11.5px] text-white/55">{t.label}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex h-[52px] items-end gap-[7px]">
                  {BAR_HEIGHTS.map((h, i) => (
                    <span
                      key={i}
                      data-cs-bar
                      className="flex-1 rounded-t-[3px] bg-gradient-to-t from-brand-600 to-brand-500/80"
                      style={{ height: `${h}%` }}
                    />
                  ))}
                </div>
              </div>

              {/* 04 · the API call */}
              <div
                data-cs-export
                className="rounded-xl border px-5 py-3.5 font-jetbrains text-[13px]"
                style={{ borderColor: CARD_BORDER, backgroundColor: "#0d0d12" }}
              >
                <div data-cs-cmd className="truncate text-white/85">
                  {EXPORT_CMD}
                </div>
                <div data-cs-result className="mt-1.5 text-data-green">
                  → 200 OK · saved rii-request-logs-acme-2026-09-28.csv
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
